import React from 'react';
import {useNavigate, useLocation} from 'react-router-dom';
import styled from 'styled-components';
import {
  HomeOutlined,
  UserAddOutlined,
  LoginOutlined,
  EnvironmentOutlined,
  CreditCardOutlined,
  SettingOutlined,
  SearchOutlined,
  SendOutlined,
  InboxOutlined,
} from '@ant-design/icons';
import NavMyRespondsIcon from "../../../media/icons/nav_my_responds.svg?react";
import {useAppSelector} from "../../../store/store";
import {groupCharterer, groupFreighter} from "../../../config/acl/roles/groups";
import {defineMessages, useIntl} from "react-intl";
import {pathFromRoute} from "../../../utils/helpers/acl/pathFromRoute";
import {Route} from "../../../config/acl/routes/Route";
import {mediaScreenSizes} from "../../../utils/constans/styles/screenSizes";

const NavMessages = defineMessages({
  signUp: { id: "mobileBottomNav.signUp", defaultMessage: "Sign up" },
  logIn: { id: "mobileBottomNav.logIn", defaultMessage: "Log in" },
  airports: { id: "mobileBottomNav.airports", defaultMessage: "Airports" },
  pricing: { id: "mobileBottomNav.pricing", defaultMessage: "Pricing" },
  home: { id: "mobileBottomNav.home", defaultMessage: "Home" },
  profile: { id: "mobileBottomNav.profile", defaultMessage: "Profile" },
  charters: { id: "mobileBottomNav.charters", defaultMessage: "Charters" },
  emptyLegs: { id: "mobileBottomNav.emptyLegs", defaultMessage: "Empty legs" },
  requests: { id: "mobileBottomNav.requests", defaultMessage: "Requests" },
  settings: { id: "mobileBottomNav.settings", defaultMessage: "Settings" },
  myOffers: { id: "mobileBottomNav.myOffers", defaultMessage: "My offers" },
});

const NavBar = styled.nav`
  display: none;
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 10px 16px;
  padding-bottom: max(10px, env(safe-area-inset-bottom));
  align-items: center;
  justify-content: center;
  pointer-events: none;

  @media (max-width: ${mediaScreenSizes.mobile}) {
    display: flex;
  }

  @media (max-width: ${mediaScreenSizes.mobileSmall}) {
    padding: 6px 10px;
    padding-bottom: max(6px, env(safe-area-inset-bottom));
  }
`;

const NavBarInner = styled.div<{ $count: number; $activeIndex: number }>`
  position: relative;
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: space-around;
  width: 100%;
  max-width: 380px;
  margin-bottom: 6px;
  padding: 6px 6px;
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.78);
  -webkit-backdrop-filter: blur(20px) saturate(180%);
  backdrop-filter: blur(20px) saturate(180%);
  border: 1px solid rgba(255, 255, 255, 0.5);
  box-shadow:
    0 4px 24px rgba(0, 0, 0, 0.08),
    0 0 1px rgba(0, 0, 0, 0.05);
  pointer-events: auto;
  --nav-count: ${(p) => p.$count};
  --nav-index: ${(p) => p.$activeIndex};

  @media (max-width: ${mediaScreenSizes.mobileSmall}) {
    padding: 4px 4px;
    margin-bottom: 4px;
    border-radius: 22px;
  }
`;

const SlidingIndicator = styled.div`
  position: absolute;
  top: 6px;
  bottom: 6px;
  left: 6px;
  width: calc((100% - 12px) / var(--nav-count));
  border-radius: 18px;
  background: rgba(0, 0, 0, 0.08);
  /* 100% here is the indicator's own width (one slot); move by index slots so bg aligns with clicked item */
  transform: translateX(calc(100% * var(--nav-index)));
  transition: transform 0.25s cubic-bezier(0.4, 0, 0.2, 1);
  pointer-events: none;
  /* Isolate on own layer so transform animation doesn't trigger reflow/shift of flex items */
  will-change: transform;

  @media (max-width: ${mediaScreenSizes.mobileSmall}) {
    top: 4px;
    bottom: 4px;
    left: 4px;
    width: calc((100% - 8px) / var(--nav-count));
    border-radius: 14px;
  }
`;

const NavItem = styled.button<{ $active?: boolean; $iconLarger?: boolean }>`
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 4px;
  flex: 1;
  min-width: 0;
  padding: 8px 6px 10px;
  font-size: 11px;
  font-weight: 600;
  line-height: 1.2;
  color: ${(p) => (p.$active ? '#1D3658' : 'rgba(0, 0, 0, 0.5)')};
  background: transparent;
  border: none;
  border-radius: 18px;
  cursor: pointer;
  transition: color 0.2s ease;

  .nav-item-icon {
    font-size: ${(p) => (p.$iconLarger ? '24px' : '20px')};
    line-height: 1;
    transition: transform 0.2s ease;
  }

  &:active .nav-item-icon {
    transform: scale(0.92);
  }

  &:active {
    background: rgba(0, 0, 0, 0.05);
  }

  @media (max-width: ${mediaScreenSizes.mobileSmall}) {
    padding: 4px 4px 6px;
    font-size: 9px;
    gap: 2px;
    border-radius: 14px;

    .nav-item-icon {
      font-size: ${(p) => (p.$iconLarger ? '20px' : '16px')};
    }
  }
`;

type NavEntry = { route: Route; labelKey: keyof typeof NavMessages; icon: React.ReactNode };

const GUEST_NAV: NavEntry[] = [
  { route: Route.home, labelKey: 'home', icon: <HomeOutlined /> },
  { route: Route.register, labelKey: 'signUp', icon: <UserAddOutlined /> },
  { route: Route.login, labelKey: 'logIn', icon: <LoginOutlined /> },
  { route: Route.airportData, labelKey: 'airports', icon: <EnvironmentOutlined /> },
  { route: Route.price, labelKey: 'pricing', icon: <CreditCardOutlined /> },
];

const AUTH_NAV: NavEntry[] = [
  { route: Route.home, labelKey: 'home', icon: <HomeOutlined /> },
  { route: Route.airportData, labelKey: 'airports', icon: <EnvironmentOutlined /> },
  { route: Route.price, labelKey: 'pricing', icon: <CreditCardOutlined /> },
  { route: Route.settings, labelKey: 'profile', icon: <SettingOutlined /> },
];

const BROKER_NAV: NavEntry[] = [
  { route: Route.newTripRequest, labelKey: 'charters', icon: <SearchOutlined /> },
  { route: Route.searchEmptyLegs, labelKey: 'emptyLegs', icon: <SendOutlined /> },
  { route: Route.requests, labelKey: 'requests', icon: <InboxOutlined /> },
  { route: Route.settings, labelKey: 'settings', icon: <SettingOutlined /> },
];

const OPERATOR_NAV: NavEntry[] = [
  { route: Route.requestsFromAgents, labelKey: 'myOffers', icon: <NavMyRespondsIcon /> },
  { route: Route.emptyLegs, labelKey: 'emptyLegs', icon: <SendOutlined /> },
  { route: Route.airportData, labelKey: 'airports', icon: <EnvironmentOutlined /> },
  { route: Route.settings, labelKey: 'settings', icon: <SettingOutlined /> },
];

export const MobileBottomNav: React.FC = () => {
  const intl = useIntl();
  const navigate = useNavigate();
  const location = useLocation();
  const isAuthenticated = useAppSelector((s) => s.auth.isAuthenticated);
  const role = useAppSelector((s) => s.user.oneItemObject.item?.role);
  const isBroker = role != null && groupCharterer.includes(role);
  const isOperator = role != null && groupFreighter.includes(role);
  const entries = isAuthenticated
    ? (isBroker ? BROKER_NAV : isOperator ? OPERATOR_NAV : AUTH_NAV)
    : GUEST_NAV;

  const settingsPath = pathFromRoute(Route.settings);
  const messageTemplatesPath = pathFromRoute(Route.messageTemplates);
  const subscriptionPath = pathFromRoute(Route.subscription);
  const eventCalendarPath = pathFromRoute(Route.eventCalendar);
  const fleetPath = pathFromRoute(Route.fleet);
  const schedulePath = pathFromRoute(Route.schedule);
  const schedulePrivatePath = pathFromRoute(Route.schedulePrivate);
  const schedulePublicPath = pathFromRoute(Route.schedulePublic);
  const isOnSettingsSection = location.pathname === settingsPath ||
    location.pathname === messageTemplatesPath ||
    location.pathname === subscriptionPath ||
    location.pathname === eventCalendarPath ||
    location.pathname === fleetPath ||
    location.pathname === schedulePath ||
    location.pathname === schedulePrivatePath ||
    location.pathname === schedulePublicPath;

  const activeIndex = entries.findIndex(({ route }) => {
    const path = pathFromRoute(route);
    if (route === Route.settings && isOnSettingsSection) return true;
    return location.pathname === path || (path !== '/' && location.pathname.startsWith(path));
  });
  const safeActiveIndex = activeIndex >= 0 ? activeIndex : 0;

  return (
    <NavBar role="navigation" aria-label="Bottom navigation">
      <NavBarInner $count={entries.length} $activeIndex={safeActiveIndex}>
        {entries.map(({ route, labelKey, icon }) => {
          const path = pathFromRoute(route);
          const isActive = (route === Route.settings && isOnSettingsSection) ||
            location.pathname === path ||
            (path !== '/' && location.pathname.startsWith(path));
          return (
            <NavItem
              key={route}
              type="button"
              $active={isActive}
              $iconLarger={route === Route.requests || route === Route.requestsFromAgents}
              onClick={() => navigate(path)}
              aria-current={isActive ? 'page' : undefined}
            >
              <span className="nav-item-icon" aria-hidden>{icon}</span>
              {intl.formatMessage(NavMessages[labelKey])}
            </NavItem>
          );
        })}
        <SlidingIndicator aria-hidden />
      </NavBarInner>
    </NavBar>
  );
};
